// Considered: EditableFilterRowMolecule — rejected because a lane condition
// (perspective/relationship/arc) has no "picker → operator → value"
// progression for that row's stepped editor to walk through; its editor is a
// single always-on value editor (PerspectiveValueEditorMolecule /
// RelationshipValueEditorMolecule / ArcValueEditorMolecule), and five lane
// rows (perspective, relationship, arc, identity, measure) share this one
// shell — see spec Reuse Audit / OQ-10 ("LaneFilterRowMolecule is a fifth new
// component"). Where that editor mounts is the `layout` prop: `'row'` (wide
// hosts) anchors it in `OverlayPanelAtom` so the tree's row rhythm stays
// stable — the shared anchor/dismiss primitive (Rule 2 — no direct
// `primereact/overlaypanel` import), the same one `LevelFilterPopoverOrganism`
// uses; `'narrow'` (column-width hosts) expands it in flow under the row,
// because a second floating layer over the popover the tree already sits in
// has nowhere to anchor (unified-filters-v3 Reuse Audit, `layout` row).
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { ArcFilterLeaf, PerspectiveFilterLeaf, RelationshipFilterLeaf } from 'types/filters';
import { getLaneForLeaf } from '../../../utils/filterGroupLeafLane';
import {
  summarizeArcFilter,
  summarizePerspectiveFilter,
  summarizeRelationshipFilter,
} from '../../../utils/filterLaneSummaries';
import type { TranslateFn } from '../../../utils/filterOperators';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import LaneMarkerAtom from '../../atoms/LaneMarker/LaneMarkerAtom';
import OverlayPanelAtom, { type OverlayPanelHandle } from '../../atoms/OverlayPanel/OverlayPanelAtom';
import ArcValueEditorMolecule from '../ArcValueEditor/ArcValueEditorMolecule';
import PerspectiveValueEditorMolecule from '../PerspectiveValueEditor/PerspectiveValueEditorMolecule';
import RelationshipValueEditorMolecule from '../RelationshipValueEditor/RelationshipValueEditorMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import './LaneFilterRowMolecule.scss';

export interface LaneFilterRowMoleculeProps {
  /** The lane condition this row renders — never re-wrapped on read or write. */
  leaf: PerspectiveFilterLeaf | RelationshipFilterLeaf | ArcFilterLeaf;
  onChange: (leaf: PerspectiveFilterLeaf | RelationshipFilterLeaf | ArcFilterLeaf) => void;
  onRemove?: () => void;
  readOnly?: boolean;
  arcTemplates?: ArcTemplateOption[];
  availableRelationships?: RelationshipOption[];
  availablePerspectives?: PerspectiveOption[];
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /** "Level 1 · Priorities" — omit outside a leveled context (e.g. the column adapter); the eyebrow then reads as just the lane name. */
  levelLabel?: string;
  /**
   * `'narrow'` splits the row into two lines — glyph/label/remove, then the
   * value read-back beneath — and expands its editor in flow instead of a
   * floating `OverlayPanelAtom` (`advanced-filters-v2.jsx` `AFNarrowRow` +
   * `AFInlinePanel`, used at column width where a second floating layer over
   * the one the row already sits in has nowhere good to anchor). Defaults to
   * `'row'`, today's single-line trigger + popover.
   */
  layout?: 'row' | 'narrow';
  /**
   * Opens the value editor on mount — a lane condition just added from the
   * catalog lands on its editor, as a property row does
   * (`EditableFilterRowMolecule` `defaultOpen`). Ignored when `readOnly`.
   */
  defaultOpen?: boolean;
  /**
   * Moves focus into the editor's first control when it opens on mount — only
   * for the one row the author just added, so a host that opens every row
   * never has each one take focus in turn. Ignored unless `defaultOpen`.
   */
  autoFocusValue?: boolean;
  /**
   * A host-detected contradiction between this row and a locked clause, in
   * words — the same contract as `EditableFilterRowMolecule`'s `problem`, so a
   * lane conflict the panel counts is also marked on the row that causes it.
   */
  problem?: string;
  /**
   * Forwarded to `PerspectiveValueEditorMolecule` for a `perspective` leaf —
   * locks the picker and starts in Specific mode (a `focusPerspective` column
   * host already fixed the perspective; see that molecule's `lockPerspective`).
   * No-op for a `relationship`/`arc` leaf.
   */
  lockPerspective?: boolean;
  /**
   * Forwarded to `ArcValueEditorMolecule` for an `arc` leaf — locks the fused
   * arc-selector chrome (a `focusArc` column host already fixed the arc lane;
   * see that molecule's `lockArcSelector`). No-op for a
   * `perspective`/`relationship` leaf.
   */
  lockArc?: boolean;
  className?: string;
}

const FOCUSABLE = 'input, textarea, select, button, [tabindex]:not([tabindex="-1"])';

/** The popover's eyebrow/preview datum ids differ per underlying leaf kind — not per lane, since the `measure` lane is a `relationship` leaf sharing `uf-rel`'s contract. */
const POPOVER_DATUMS: Record<'perspective' | 'relationship' | 'arc', { eyebrow: string; glyph: string; statusMark: string }> = {
  perspective: {
    eyebrow: 'uf-persp.level-1-priorities-perspective-filter',
    glyph: 'uf-persp.glyph',
    statusMark: 'uf-persp.status-mark',
  },
  relationship: {
    eyebrow: 'uf-rel.level-1-priorities-relationship-filter',
    glyph: 'uf-rel.glyph',
    statusMark: 'uf-rel.status-mark',
  },
  arc: {
    eyebrow: 'uf-arc.level-1-priorities-arc-filter',
    glyph: 'uf-arc.glyph',
    statusMark: 'uf-arc.status-mark#2',
  },
};

/** The row's one-line read-back of its lane condition, via that leaf kind's own summarizer. */
function summarizeLaneLeaf(
  leaf: LaneFilterRowMoleculeProps['leaf'],
  t: TranslateFn,
  availablePerspectives: PerspectiveOption[],
  availableRelationships: RelationshipOption[]
): string {
  switch (leaf.kind) {
    case 'perspective':
      return summarizePerspectiveFilter(leaf.filter, t, availablePerspectives);
    case 'relationship':
      return summarizeRelationshipFilter(leaf.filter, t, availableRelationships);
    default:
      return summarizeArcFilter(leaf.filter, t);
  }
}

function buildRootClassName(narrow: boolean, hasProblem: boolean, className: string): string {
  const classes = ['lane-filter-row-molecule'];
  if (narrow) classes.push('lane-filter-row-molecule--narrow');
  if (hasProblem) classes.push('lane-filter-row-molecule--problem');
  if (className) classes.push(className);
  return classes.join(' ');
}

/**
 * One lane condition (perspective / relationship / arc — including a measure
 * row, a `relationship` leaf presented under the `measure` lane) as a tree
 * row: a `LaneMarkerAtom` glyph, a one-line summary, and a remove affordance,
 * with the lane's full value editor anchored below as a popover. The five
 * lane rows (identity is a property leaf via `EditableFilterRowMolecule`, not
 * this molecule — see `types/filterLane.ts`) share this one shell.
 */
const LaneFilterRowMolecule: React.FC<LaneFilterRowMoleculeProps> = ({
  leaf,
  onChange,
  onRemove,
  readOnly = false,
  arcTemplates = [],
  availableRelationships = [],
  availablePerspectives = [],
  perspectiveSearchProps,
  levelLabel,
  layout = 'row',
  defaultOpen = false,
  autoFocusValue = false,
  problem,
  lockPerspective = false,
  lockArc = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<OverlayPanelHandle>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const narrow = layout === 'narrow';
  const openOnMount = defaultOpen && !readOnly;
  const [narrowOpen, setNarrowOpen] = useState(narrow && openOnMount);
  // Mirrors the row layout's popover visibility for the trigger's `aria-expanded`.
  const [overlayOpen, setOverlayOpen] = useState(false);
  const narrowEditorId = useId();
  // Focus is taken once, on the editor's first opening — never on a later reopen.
  const pendingFocusRef = useRef(openOnMount && autoFocusValue);
  const focusEditorOnce = () => {
    if (!pendingFocusRef.current) return;
    pendingFocusRef.current = false;
    editorRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  };

  useEffect(() => {
    if (!openOnMount) return;
    if (narrow) focusEditorOnce();
    else overlayRef.current?.show(null, triggerRef.current);
    // Mount-only: `defaultOpen` describes how the row first appears; a later
    // prop change must not re-open an editor the author closed.
  }, []);
  const lane = getLaneForLeaf(leaf);
  const popoverDatums = POPOVER_DATUMS[leaf.kind];
  const laneName = t(`filters.lane.${lane}.badge`);
  const eyebrowText = levelLabel
    ? t('filters.laneRow.eyebrowWithLevel', { level: levelLabel, lane: laneName })
    : t('filters.laneRow.eyebrow', { lane: laneName });

  const summary = summarizeLaneLeaf(leaf, t, availablePerspectives, availableRelationships);

  // Both triggers are named by what they show — the lane and its summary — so a
  // screen reader tells one lane condition from another instead of hearing a
  // generic "Edit filter" on each. (The row trigger's own content would repeat
  // the lane, since its LaneMarkerAtom is itself labelled with the lane name.)
  const triggerLabel = t('filters.laneRow.triggerLabel', {
    lane: laneName,
    summary: summary || t('filters.laneRow.notConfigured'),
  });

  const summaryContent = (
    <>
      {summary && leaf.kind === 'perspective' && (
        <ChipAtom
          label={summary}
          className="lane-filter-row-molecule__summary-chip"
          data-container="each(value.perspectives)"
          data-datum="uf-persp.chip"
          data-kind="chip"
        />
      )}
      {/* uf-arc.chip (EAINT-908 Phase 3) — the board's per-row ACChip
          (arc-filter.jsx:538). Our tree puts one arc condition per leaf
          rather than a lane holding several `value.arcs` rows, so this
          leaf's own summary chip is that datum's equivalent. */}
      {summary && leaf.kind === 'arc' && (
        <ChipAtom
          label={summary}
          className="lane-filter-row-molecule__summary-chip"
          data-container="each(value.arcs)"
          data-datum="uf-arc.chip"
          data-kind="chip"
        />
      )}
      {/* uf-rel.chip (EAINT-908 Phase 3) — the board's per-row RFChip
          (relationship-filter.jsx:692), shown once the row's relationship
          is configured (`row.rel` — matches uf-persp.chip/uf-arc.chip). */}
      {summary && leaf.kind === 'relationship' && (
        <ChipAtom
          label={summary}
          className="lane-filter-row-molecule__summary-chip"
          data-container="each(value.relationships)"
          data-datum="uf-rel.chip"
          data-kind="chip"
        />
      )}
      {!summary && (
        <span className="lane-filter-row-molecule__summary lane-filter-row-molecule__summary--placeholder">
          {t('filters.laneRow.notConfigured')}
        </span>
      )}
    </>
  );

  const editorBody = (
    <div
      ref={editorRef}
      id={narrow ? narrowEditorId : undefined}
      className="lane-filter-row-molecule__editor"
      data-container="ui-lane-config"
    >
      <div className="lane-filter-row-molecule__eyebrow" data-datum={popoverDatums.eyebrow} data-kind="label">
        <span className="lane-filter-row-molecule__eyebrow-glyph" data-datum={popoverDatums.glyph} data-kind="glyph">
          <LaneMarkerAtom lane={lane} size="sm" />
        </span>
        <span className="lane-filter-row-molecule__eyebrow-text">{eyebrowText}</span>
      </div>
      {leaf.kind === 'perspective' && (
        <PerspectiveValueEditorMolecule
          availablePerspectives={availablePerspectives}
          value={leaf.filter}
          onChange={(next) => onChange({ kind: 'perspective', filter: next })}
          readOnly={readOnly}
          perspectiveSearchProps={perspectiveSearchProps}
          lockPerspective={lockPerspective}
          initialMode={lockPerspective ? 'specific' : undefined}
        />
      )}
      {leaf.kind === 'relationship' && (
        // `lockRelationship` is forwarded only for the measure lane — it
        // locks the edge (`measures_into`) but leaves operator/direction/
        // minCount/relatedTo (the aggregate roster) editable.
        <RelationshipValueEditorMolecule
          availableRelationships={availableRelationships}
          value={leaf.filter}
          onChange={(next) => onChange({ kind: 'relationship', filter: next })}
          readOnly={readOnly}
          lockRelationship={lane === 'measure'}
        />
      )}
      {leaf.kind === 'arc' && (
        <ArcValueEditorMolecule
          arcTemplates={arcTemplates}
          value={leaf.filter}
          onChange={(next) => onChange({ kind: 'arc', filter: next })}
          readOnly={readOnly}
          lockArcSelector={lockArc}
        />
      )}
      <div className="lane-filter-row-molecule__preview">
        <span
          className="lane-filter-row-molecule__preview-dot"
          data-datum={popoverDatums.statusMark}
          data-kind="status-mark"
          aria-hidden="true"
        />
        <span className="lane-filter-row-molecule__preview-text">{t('filters.laneRow.previewHint')}</span>
      </div>
    </div>
  );

  const removeButton = !readOnly && onRemove && (
    <ButtonAtom
      type="button"
      icon="pi pi-times"
      variant="ghost"
      shape="icon-square"
      size="small"
      onClick={onRemove}
      aria-label={t('filterEditor.row.removeFilter')}
      tooltip={t('filterEditor.row.removeFilter')}
      data-testid="lane-filter-row-remove"
    />
  );

  const rootClassName = buildRootClassName(narrow, Boolean(problem), className);

  // A host-detected contradiction, shown the way a property row shows it
  // (`EditableFilterRowMolecule`): an icon on the summary the row is keyed to,
  // and the finding in words underneath. Without this a lane conflict counts
  // toward the panel's problem badge with no row marked, so the person is told
  // something is wrong and given nothing to fix (ux-craft S2).
  const problemIcon = problem ? (
    <i
      className="pi pi-exclamation-triangle lane-filter-row-molecule__problem-icon"
      title={problem}
      aria-hidden="true"
      data-testid="lane-filter-row-problem-icon"
    />
  ) : null;

  const problemMessage = problem ? (
    <div className="lane-filter-row-molecule__problem" data-testid="lane-filter-row-problem">
      {problem}
    </div>
  ) : null;

  if (narrow) {
    return (
      <div
        className={rootClassName}
        data-testid="lane-filter-row"
        data-kind="lane-leaf"
        data-lane={lane}
        data-container="ui-lane-config"
      >
        <div className="lane-filter-row-molecule__header">
          {problemIcon}
          <LaneMarkerAtom lane={lane} size="md" />
          <span className="lane-filter-row-molecule__label">{laneName}</span>
          <span className="lane-filter-row-molecule__spacer" />
          {removeButton}
        </div>
        <button
          type="button"
          className="lane-filter-row-molecule__value-toggle"
          onClick={() => !readOnly && setNarrowOpen((open) => !open)}
          disabled={readOnly}
          aria-expanded={narrowOpen}
          aria-controls={narrowOpen ? narrowEditorId : undefined}
          aria-label={triggerLabel}
          data-testid="lane-filter-row-trigger"
        >
          {summaryContent}
        </button>
        {problemMessage}
        {narrowOpen && editorBody}
      </div>
    );
  }

  return (
    <div
      className={rootClassName}
      data-testid="lane-filter-row"
      data-kind="lane-leaf"
      data-lane={lane}
      data-container="ui-lane-config"
    >
      <button
        ref={triggerRef}
        type="button"
        className="lane-filter-row-molecule__trigger"
        onClick={(e) => !readOnly && overlayRef.current?.toggle(e)}
        disabled={readOnly}
        aria-haspopup="dialog"
        aria-expanded={overlayOpen}
        aria-label={triggerLabel}
        data-testid="lane-filter-row-trigger"
      >
        {problemIcon}
        <LaneMarkerAtom lane={lane} size="md" />
        <span className="lane-filter-row-molecule__label">{laneName}</span>
        {summaryContent}
      </button>
      <span className="lane-filter-row-molecule__spacer" />
      {removeButton}

      {problemMessage}

      <OverlayPanelAtom
        ref={overlayRef}
        className="lane-filter-row-molecule__overlay"
        dismissable
        role="dialog"
        aria-label={eyebrowText}
        onShow={() => {
          setOverlayOpen(true);
          focusEditorOnce();
        }}
        onHide={() => setOverlayOpen(false)}
      >
        {editorBody}
      </OverlayPanelAtom>
    </div>
  );
};

LaneFilterRowMolecule.displayName = 'LaneFilterRowMolecule';

export default LaneFilterRowMolecule;
